import { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '@/app/store/hooks';
import { fetchContent } from '@/entities/home_content/api/fetchSiteContent';
import { fetchBuildings } from '@/entities/knowledge/api/fetchKnowledge';
import { preloadImagesRobustly } from '@shared/lib/robustImageLoader';

interface PreloadStep {
  id: string;
  name: string;
  weight: number; // Percentage of total progress
  execute: () => Promise<void>;
}

interface UseAppPreloaderReturn {
  progress: number;
  status: string;
  isComplete: boolean;
  error: string | null;
  startPreloading: () => void;
}

// Critical images that must be loaded before showing the app
const CRITICAL_IMAGES = [
  '/masks/reverse_pill_mask.png',
  '/masks/reverse_pill_mask_mini.png',
  '/masks/map_card_mask.png',
  '/masks/feedback/pc.png',
  '/images/knowledge_card_default_bg.jpg',
  '/images/feedback_bg.png',
  '/images/type_of_buildings/retail.png',
  '/images/type_of_buildings/business.png',
];

export const useAppPreloader = (): UseAppPreloaderReturn => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('Загрузка данных...');
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isStarted, setIsStarted] = useState(false);

  const dispatch = useAppDispatch();

  const createPreloadSteps = useCallback(
    (): PreloadStep[] => [
      {
        id: 'site-content',
        name: 'Загрузка контента сайта',
        weight: 50,
        execute: async () => {
          const result = await dispatch(fetchContent());
          if (fetchContent.rejected.match(result)) {
            console.warn('Site content failed to load, but continuing...');
            // Don't throw error - let the page handle it
          }
        },
      },
      {
        id: 'buildings-data',
        name: 'Загрузка данных о зданиях',
        weight: 40,
        execute: async () => {
          const result = await dispatch(fetchBuildings());
          if (fetchBuildings.rejected.match(result)) {
            console.warn('Buildings data failed to load, but continuing...');
            // Don't throw error - let the page handle it
          }
        },
      },
      {
        id: 'background-cache',
        name: 'Кэширование изображений',
        weight: 10,
        execute: async () => {
          // Start background caching but don't wait for it
          preloadImagesRobustly(CRITICAL_IMAGES, {
            retries: 1,
            timeout: 3000, // Reduced timeout to fail faster
            fallbackToHttp1: true,
            useCache: true,
          })
            .then((results) => {
              const successCount = results.filter((r) => r.success).length;
              const warningCount = results.filter(
                (r) => !r.success && r.error?.includes('HTTP2_PROTOCOL_ERROR')
              ).length;

              console.log(
                `Background cache: ${successCount}/${CRITICAL_IMAGES.length} images cached`
              );

              if (warningCount > 0) {
                console.warn(
                  `${warningCount} images had HTTP/2 protocol warnings but may have loaded successfully`
                );
              }
            })
            .catch((error) => {
              console.warn('Background image caching failed:', error);
            });

          // Don't wait - just start the process
          await new Promise((resolve) => setTimeout(resolve, 100));
        },
      },
    ],
    [dispatch]
  );

  const startPreloading = useCallback(async () => {
    if (isStarted) return;

    setIsStarted(true);
    setProgress(0);
    setError(null);
    setIsComplete(false);

    // Maximum preload time - force complete after 8 seconds (only data loading)
    const maxPreloadTime = setTimeout(() => {
      console.warn('Preloader taking too long, forcing completion...');
      setProgress(100);
      setStatus('Готово!');
      setIsComplete(true);
    }, 8000);

    try {
      const steps = createPreloadSteps();
      let currentProgress = 0;

      for (const step of steps) {
        setStatus(step.name);

        try {
          await step.execute();
          currentProgress += step.weight;
          setProgress(Math.min(currentProgress, 100));

          // Small delay between steps for smooth progress
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (stepError: any) {
          console.error(`Preload step "${step.id}" failed:`, stepError);

          // Continue with all steps - don't block on any failures
          console.warn(`Step failed, continuing: ${stepError.message}`);
          currentProgress += step.weight;
          setProgress(Math.min(currentProgress, 100));
        }
      }

      clearTimeout(maxPreloadTime);
      setProgress(100);
      setStatus('Готово!');

      // Mark as complete immediately
      setIsComplete(true);
    } catch (error: any) {
      clearTimeout(maxPreloadTime);
      console.error('Preloading failed:', error);
      // Don't set error - just complete anyway
      console.warn('Preloader failed, but showing app anyway');
      setProgress(100);
      setStatus('Готово!');
      setIsComplete(true);
    }
  }, [isStarted, createPreloadSteps]);

  return {
    progress,
    status,
    isComplete,
    error,
    startPreloading,
  };
};
